import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarIntegrationType } from "@/types/calendar";
import { useCalApi } from "./useCalApi";

type DeleteCalendarCredentialParams = {
  id: number;
  calendar: CalendarIntegrationType;
};

type UseDeleteCalendarCredentialOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

type UseDeleteCalendarCredentialResult = {
  mutate: (params: DeleteCalendarCredentialParams) => Promise<void>;
  isPending: boolean;
};

/**
 * Hook to delete/disconnect a calendar credential.
 * Replaces @calcom/atoms useDeleteCalendarCredentials hook.
 */
export function useDeleteCalendarCredential(
  options: UseDeleteCalendarCredentialOptions = {}
): UseDeleteCalendarCredentialResult {
  const { fetchCalApi } = useCalApi();
  const [isPending, setIsPending] = useState(false);

  // Use refs to avoid recreating mutate when callbacks change
  const onSuccessRef = useRef(options.onSuccess);
  const onErrorRef = useRef(options.onError);

  // Keep refs up to date
  useEffect(() => {
    onSuccessRef.current = options.onSuccess;
    onErrorRef.current = options.onError;
  }, [options.onSuccess, options.onError]);

  const mutate = useCallback(
    async (params: DeleteCalendarCredentialParams) => {
      setIsPending(true);
      try {
        const result = await fetchCalApi<{ success: boolean }>("/calendars", {
          method: "DELETE",
          body: {
            credentialId: params.id,
            integration: params.calendar,
          },
        });

        if (result?.success) {
          onSuccessRef.current?.();
        } else {
          onErrorRef.current?.(new Error("Failed to disconnect calendar"));
        }
      } catch (error) {
        onErrorRef.current?.(error instanceof Error ? error : new Error("Unknown error"));
      } finally {
        setIsPending(false);
      }
    },
    [fetchCalApi]
  );

  return {
    mutate,
    isPending,
  };
}
